import {
  Button,
  ControlledSelect,
  ControlledInput,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle
} from '@hm/fundamental'
import {useEffect, useMemo, useState} from 'react'

import {isValidPlantForImport, resolveSiteNameForPlant, type ImportPlantOption} from '../../helpers'
import {useRiskSurveyTranslation} from '../../hooks'
import type {ReportSummaryDto, UpdateReportSiteRequest} from '../../types'

type EditSiteModalProps = {
  open: boolean
  report: ReportSummaryDto | null
  plantOptions: ImportPlantOption[]
  isLoading: boolean
  onClose: () => void
  onConfirm: (data: UpdateReportSiteRequest) => void
}

export const EditSiteModal = ({
  open,
  report,
  plantOptions,
  isLoading,
  onClose,
  onConfirm
}: EditSiteModalProps) => {
  const {t} = useRiskSurveyTranslation()

  const [upmId, setUpmId] = useState('')
  const [siteName, setSiteName] = useState('')

  // Pre-fill from the report whenever a new report is opened for editing.
  useEffect(() => {
    setUpmId(report?.upmId ?? '')
    setSiteName(report?.siteName ?? '')
  }, [report])

  const handlePlantChange = (value: string) => {
    setUpmId(value)
    // Auto-resolve the site name from the newly selected plant.
    const resolved = resolveSiteNameForPlant(value, plantOptions)
    if (resolved) {
      setSiteName(resolved)
    }
  }

  const isPlantValid = useMemo(
    () => isValidPlantForImport(upmId, plantOptions),
    [upmId, plantOptions]
  )
  const initialUpmId = report?.upmId ?? ''
  const initialSiteName = (report?.siteName ?? '').trim()
  const isSiteNameValid = siteName.trim().length > 0
  const hasChanges = upmId !== initialUpmId || siteName.trim() !== initialSiteName
  const canSubmit = isPlantValid && isSiteNameValid && hasChanges && !isLoading

  const handleConfirm = () => {
    if (!canSubmit) {
      return
    }
    onConfirm({
      upmId,
      siteName: siteName.trim(),
      location: report?.location ?? null
    })
  }

  return (
    <Modal open={open} onClose={onClose} width="thin" data-cy="edit-site-modal">
      <ModalTitle>{t('editSite.title')}</ModalTitle>
      <ModalContent>
        <div className="flex flex-col gap-4">
          <ControlledSelect
            name="editSitePlant"
            label={t('editSite.plant')}
            value={upmId}
            onChange={(value) => handlePlantChange(String(value))}
            values={[
              {value: '', label: t('common.select')},
              ...plantOptions.map((option) => ({
                value: option.upmId,
                label: `${option.siteName} (${option.plantId})`
              }))
            ]}
            state={!isPlantValid ? 'error' : undefined}
            error={!isPlantValid ? t('editSite.invalidPlantSelection') : undefined}
            data-cy="edit-site-plant"
          />
          <ControlledInput
            name="editSiteName"
            label={t('editSite.siteName')}
            value={siteName}
            onChange={(event) => setSiteName(event.target.value)}
            isTouched={!isSiteNameValid}
            error={!isSiteNameValid ? t('editSite.siteNameRequired') : undefined}
            data-cy="edit-site-name"
          />
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="tertiary" onClick={onClose} data-cy="edit-site-cancel-button">
          {t('common.cancel')}
        </Button>
        <Button onClick={handleConfirm} disabled={!canSubmit} data-cy="edit-site-confirm-button">
          {t('editSite.confirm')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
